import { useMemo } from 'react';

import { cn, ConditionalRender, Skeleton, Stack } from '@crossnokaye/ux-common';

import {
  AnimaticValueShelfItem,
  AnimaticValueShelfItemVariant,
  ValuePosition,
} from '@repo/sdk/models/components';

import { useGetRefValue } from '@/features/equipment-browser/subscriptions/use-get-ref-value';
import { formatUnits } from '@/helpers/unit-formatter/unit-formatter';

import { AnimaticShelfCarousel } from './animatic-shelf-carousel';

type ShelfValueSectionProps = {
  isMobile?: boolean;
  value: AnimaticValueShelfItem;
  isStatus?: boolean;
};

const valueItemVariants: Record<AnimaticValueShelfItemVariant, string> = {
  stage: 'bg-ckds-base-white rounded p-0.5 text-background',
  name: 'bg-ckds-neutral-200 rounded p-0.5 text-background',
  default: 'text-foreground',
  not_in_sequence: 'text-foreground py-3',
};

export const ShelfValueSection = ({ value, isStatus = false }: ShelfValueSectionProps) => {
  const variant = value.variant ?? AnimaticValueShelfItemVariant.Default;

  // Reactively get the ref value from Jotai atoms
  const refValue = useGetRefValue(value.ref);

  const displayValue = useMemo(() => {
    let display = value.value ?? '--';
    if (refValue != null) {
      const formattedLabels = formatUnits({ value: refValue, units: value.ref?.units });
      display = formattedLabels.valueUnitsLabel;
    }

    if (value.valueFormatter && display) {
      display = value.valueFormatter.replace('%s', display);
    }
    return display;
  }, [refValue, value]);

  return (
    <div className="flex items-center gap-2 px-2 py-0.5 whitespace-nowrap">
      <ConditionalRender condition={isStatus}>
        <span className="text-sm font-bold text-muted-foreground leading-5 @2xl:text-sm">
          {'STATUS:'}
        </span>
      </ConditionalRender>
      <span
        className={cn(
          valueItemVariants[variant],
          'text-sm font-bold leading-5 @2xl:text-sm truncate uppercase'
        )}
      >
        {displayValue}
      </span>
    </div>
  );
};

type AnimaticShelfValuesProps = {
  values: AnimaticValueShelfItem[];
  isMobile: boolean;
  isStatus?: boolean;
};

/**
 * Value shelves use one carousel for the whole top+bottom stack so a single prev/next
 * control set paginates aligned columns (top and status rows scroll together).
 */
export const AnimaticShelfValues = ({
  values,
  isMobile,
}: AnimaticShelfValuesProps): React.ReactNode => {
  const topValues = useMemo(
    () => values.filter((value) => value.valuePosition === ValuePosition.Top),
    [values]
  );
  const bottomValues = useMemo(
    () => values.filter((value) => value.valuePosition === ValuePosition.Bottom),
    [values]
  );

  const visibleTop = useMemo(() => topValues.filter((value) => !value.hidden), [topValues]);
  const visibleBottom = useMemo(
    () => bottomValues.filter((value) => !value.hidden),
    [bottomValues]
  );

  const slideCount = Math.max(visibleTop.length, visibleBottom.length);

  const carouselItems = useMemo(() => {
    if (slideCount === 0) {
      return [];
    }

    return Array.from({ length: slideCount }, (_, index) => {
      const topValue = visibleTop[index];
      const bottomValue = visibleBottom[index];
      const key = topValue?.ref?.id ?? bottomValue?.ref?.id ?? `shelf-value-column-${index}`;

      return {
        key,
        content: (
          <Stack className="min-w-0" gap="thin">
            {topValue ? <ShelfValueSection value={topValue} isMobile={isMobile} /> : null}
            {bottomValue ? (
              <ShelfValueSection value={bottomValue} isMobile={isMobile} isStatus={true} />
            ) : null}
          </Stack>
        ),
      };
    });
  }, [isMobile, slideCount, visibleBottom, visibleTop]);

  if (carouselItems.length === 0) {
    return null;
  }

  return (
    <AnimaticShelfCarousel
      items={carouselItems}
      itemLayout="contentWidth"
      frameClassName="p-1 @2xl:p-0.5"
    />
  );
};

export const ValueShelfSkeletons = () => {
  return <Skeleton className="w-full h-4" />;
};
